/**
 * Currency configuration for LUKHAS pricing
 *
 * IMPORTANT: Exchange rates shown are for DISPLAY PURPOSES ONLY.
 * Actual charges are processed by Stripe at current market rates.
 *
 * USD is default, auto-detect EUR for EU, GBP for UK.
 */

export type CurrencyCode = 'USD' | 'EUR' | 'GBP'

export interface CurrencyConfig {
  code: CurrencyCode
  symbol: string
  name: string
  locale: string // For Intl.NumberFormat
  // Static display rates (Stripe handles actual conversion)
  // DISPLAY ONLY - not used for actual billing
  rateFromUSD: number
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    rateFromUSD: 1.0,
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE', // German locale for EUR formatting
    rateFromUSD: 0.92, // DISPLAY ONLY - approximate rate
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    rateFromUSD: 0.79, // DISPLAY ONLY - approximate rate
  },
}

/**
 * FX Disclaimer text for non-USD currencies.
 * MUST be displayed when showing converted prices.
 */
export const FX_DISCLAIMER =
  'Prices shown are approximate. Final charge will be in USD at current exchange rates.'

/**
 * Short FX disclaimer for compact displays
 */
export const FX_DISCLAIMER_SHORT = 'Approx. Final charge in USD.'

// EU country codes for geo-detection
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]

// EU timezone prefixes for fallback detection
const EU_TIMEZONES = ['Europe/']
const UK_TIMEZONES = ['Europe/London', 'Europe/Belfast']

/**
 * Detect user's likely currency based on timezone/locale
 * Falls back to USD if detection fails
 */
export function detectCurrency(): CurrencyCode {
  try {
    // Try to get country from Intl API
    const locale = navigator.language || 'en-US'
    const regionCode = locale.split('-')[1]?.toUpperCase()

    // UK detection
    if (regionCode === 'GB' || regionCode === 'UK') {
      return 'GBP'
    }

    // EU detection by region code
    if (regionCode && EU_COUNTRIES.includes(regionCode)) {
      return 'EUR'
    }

    // Fallback: timezone-based detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // UK timezones
    if (UK_TIMEZONES.includes(timezone)) {
      return 'GBP'
    }

    // EU timezones (but not UK)
    if (EU_TIMEZONES.some(prefix => timezone.startsWith(prefix)) && !UK_TIMEZONES.includes(timezone)) {
      return 'EUR'
    }

    // Ireland specifically uses EUR
    if (timezone === 'Europe/Dublin') {
      return 'EUR'
    }

  } catch {
    // Detection failed, use default
  }

  return 'USD'
}

/**
 * Intl.NumberFormat cache for performance
 */
const formattersCache = new Map<string, Intl.NumberFormat>()

function getFormatter(
  currency: CurrencyCode,
  showDecimals: boolean
): Intl.NumberFormat {
  const cacheKey = `${currency}-${showDecimals}`

  if (!formattersCache.has(cacheKey)) {
    const config = CURRENCIES[currency]
    formattersCache.set(
      cacheKey,
      new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.code,
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      })
    )
  }

  return formattersCache.get(cacheKey)!
}

/**
 * Format a price using Intl.NumberFormat for proper localization.
 *
 * IMPORTANT: For non-USD currencies, this shows approximate display prices.
 * Actual charges are processed by Stripe at current market rates.
 *
 * @param amountUSD - Price in USD (base currency)
 * @param currency - Target display currency
 * @param showDecimals - Whether to show decimal places (default: false)
 * @returns Formatted price string (e.g., "$20", "€18", "£16")
 */
export function formatPrice(
  amountUSD: number,
  currency: CurrencyCode = 'USD',
  showDecimals = false
): string {
  const config = CURRENCIES[currency]
  const converted = amountUSD * config.rateFromUSD

  try {
    const formatter = getFormatter(currency, showDecimals)
    return formatter.format(converted)
  } catch {
    // Fallback for environments without Intl support
    const displayAmount = showDecimals
      ? converted.toFixed(2)
      : Math.round(converted).toString()
    return `${config.symbol}${displayAmount}`
  }
}

/**
 * Format price with explicit "approx" indicator for non-USD
 * Use this when FX disclaimer is not visible nearby.
 */
export function formatPriceWithIndicator(
  amountUSD: number,
  currency: CurrencyCode = 'USD',
  showDecimals = false
): string {
  const formatted = formatPrice(amountUSD, currency, showDecimals)

  if (currency !== 'USD') {
    return `~${formatted}` // Tilde indicates approximate
  }

  return formatted
}

/**
 * Check if current currency requires FX disclaimer
 */
export function requiresFxDisclaimer(currency: CurrencyCode): boolean {
  return currency !== 'USD'
}

/**
 * Get currency config by code
 */
export function getCurrencyConfig(code: CurrencyCode): CurrencyConfig {
  return CURRENCIES[code]
}

/**
 * Storage key for user's currency preference
 */
const CURRENCY_STORAGE_KEY = 'lukhas_currency'

/**
 * Get user's currency preference (from storage or auto-detect)
 */
export function getUserCurrency(): CurrencyCode {
  try {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY)
    if (stored && stored in CURRENCIES) {
      return stored as CurrencyCode
    }
  } catch {
    // localStorage not available
  }
  return detectCurrency()
}

/**
 * Save user's currency preference
 */
export function setUserCurrency(currency: CurrencyCode): void {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency)
  } catch {
    // localStorage not available
  }
}
